import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import Layout from "../components/Layout";
import { CircleNotch, Table as TableIcon, WarningCircle, Database, ArrowRight, Plugs } from "@phosphor-icons/react";

// Drill-through only applies to the v2 proxy (records carry a v2 id).
const DRILL = {
  tasks: (it) => `/jobs/${it.id}`,
  quotes: (it) => `/quotes/${it.id}`,
  clients: (it) => `/clients/${it.id}`,
};

function flatten(obj, prefix = "", out = {}) {
  for (const [k, v] of Object.entries(obj || {})) {
    const key = prefix ? `${prefix}.${k}` : k;
    if (k === "_links") continue;
    if (v && typeof v === "object" && !Array.isArray(v)) {
      if (v.name || v.description || v.shortName || v.clientname || v.categoryname || v.orgname) {
        out[key] = v.name || v.description || v.shortName || v.clientname || v.categoryname || v.orgname;
      }
    } else if (Array.isArray(v)) {
      out[key] = `${v.length} item(s)`;
    } else {
      out[key] = v;
    }
  }
  return out;
}

function cellText(val) {
  if (val === null || val === undefined || val === "") return "—";
  if (typeof val === "boolean") return val ? "Yes" : "No";
  return String(val);
}

const APIS = [
  { id: "v2", label: "Aroflo API (v2)", sub: "Bearer token" },
  { id: "v1", label: "Aroflo API (v1)", sub: "HMAC-SHA512" },
];

export default function DataViewer() {
  const navigate = useNavigate();
  const [apiVersion, setApiVersion] = useState(""); // "" until chosen
  const [sections, setSections] = useState([]);
  const [sectionsLoading, setSectionsLoading] = useState(false);
  const [active, setActive] = useState("");
  const [rows, setRows] = useState([]);
  const [rawItems, setRawItems] = useState([]);
  const [count, setCount] = useState(0);
  const [columns, setColumns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Load the section list for the chosen API — but do NOT auto-select or auto-load data.
  useEffect(() => {
    setSections([]);
    setActive("");
    setRows([]);
    setRawItems([]);
    setColumns([]);
    setError("");
    setCount(0);
    if (!apiVersion) return;
    setSectionsLoading(true);
    const req = apiVersion === "v2"
      ? api.get("/aroflo/categories").then((r) => r.data.categories.filter((c) => c.status === "available").map((c) => ({ id: c.id, label: c.label })))
      : api.get("/legacy/zones").then((r) => r.data.zones.map((z) => ({ id: z.id, label: z.label })));
    req.then(setSections).catch(() => setSections([])).finally(() => setSectionsLoading(false));
  }, [apiVersion]);

  const fetchData = useCallback(async (version, catId) => {
    if (!version || !catId) return;
    setLoading(true);
    setError("");
    setRows([]);
    setRawItems([]);
    setColumns([]);
    try {
      let items = [];
      let total = 0;
      if (version === "v2") {
        const { data } = await api.get(`/aroflo/data/${catId}`, { params: { page: 1, limit: 25 } });
        items = data.items || [];
        total = data.count || items.length;
      } else {
        const { data } = await api.get(`/legacy/zone/${catId}`, { params: { page: 1, page_size: 25 } });
        if (!data.ok) throw new Error(data.status_message || data.error || "The Aroflo v1 API returned an error for this zone.");
        items = data.records || [];
        total = data.total || items.length;
      }
      const flat = items.map((it) => flatten(it));
      const colSet = [];
      flat.forEach((row) => Object.keys(row).forEach((k) => { if (!colSet.includes(k)) colSet.push(k); }));
      setColumns(colSet.slice(0, 9));
      setRows(flat);
      setRawItems(items);
      setCount(total);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (apiVersion && active) fetchData(apiVersion, active);
  }, [apiVersion, active, fetchData]);

  const drillKey = apiVersion === "v2" ? active : null;

  return (
    <Layout>
      <div data-testid="data-viewer-page">
        <p className="text-sm uppercase tracking-widest font-semibold text-neutral-500">Live Records</p>
        <h1 className="mt-1 font-heading text-4xl sm:text-5xl font-black tracking-tighter text-[#0A0A0A]">Data Viewer</h1>
        <p className="mt-3 max-w-3xl text-base text-neutral-600">
          Live records pulled directly from your Aroflo system. First choose an API, then pick a section to load its data.
        </p>

        {/* Step 1 — choose API */}
        <div className="mt-6">
          <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest text-neutral-500">
            <Plugs size={14} weight="bold" /> 1 · Select API
          </p>
          <div className="flex flex-wrap gap-3">
            {APIS.map((opt) => (
              <button key={opt.id} data-testid={`viewer-api-${opt.id}`} onClick={() => setApiVersion(opt.id)}
                className={`flex flex-col items-start rounded-sm border px-4 py-2.5 text-left transition-all duration-200 ${apiVersion === opt.id ? "border-[#0055FF] bg-[#0055FF]/[0.04] ring-1 ring-[#0055FF]" : "border-neutral-300 bg-white hover:bg-neutral-50"}`}>
                <span className={`text-sm font-semibold ${apiVersion === opt.id ? "text-[#0055FF]" : "text-[#0A0A0A]"}`}>{opt.label}</span>
                <span className="text-xs text-neutral-500">{opt.sub}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Step 2 — choose section */}
        {apiVersion && (
          <div className="mt-6" data-testid="viewer-section-picker">
            <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest text-neutral-500">
              <TableIcon size={14} weight="bold" /> 2 · Select section
            </p>
            {sectionsLoading ? (
              <div className="flex h-12 items-center gap-2 text-sm text-neutral-500"><CircleNotch size={18} className="animate-spin text-[#0055FF]" /> Loading sections…</div>
            ) : (
              <div className="flex flex-wrap gap-2">
                {sections.map((c) => (
                  <button key={c.id} data-testid={`viewer-tab-${c.id}`} onClick={() => setActive(c.id)}
                    className={`rounded-sm px-4 py-2 text-sm font-semibold transition-all duration-200 ${active === c.id ? "bg-[#0A0A0A] text-white" : "border border-neutral-300 bg-white text-neutral-700 hover:bg-neutral-50"}`}>
                    {c.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Prompt / data area */}
        {!apiVersion ? (
          <div className="mt-6 flex h-56 flex-col items-center justify-center gap-2 rounded-sm border border-dashed border-neutral-300 bg-white" data-testid="viewer-prompt-api">
            <Plugs size={28} className="text-neutral-300" />
            <p className="text-sm text-neutral-500">Select an API above to begin.</p>
          </div>
        ) : !active ? (
          <div className="mt-6 flex h-56 flex-col items-center justify-center gap-2 rounded-sm border border-dashed border-neutral-300 bg-white" data-testid="viewer-prompt-section">
            <TableIcon size={28} className="text-neutral-300" />
            <p className="text-sm text-neutral-500">Select a section to load its data.</p>
          </div>
        ) : (
          <div className="mt-5 border border-neutral-200 bg-white shadow-sm rounded-sm">
            <div className="flex items-center justify-between border-b border-neutral-200 px-5 py-3">
              <div className="flex items-center gap-2">
                <TableIcon size={18} className="text-neutral-500" />
                <span className="text-sm font-semibold text-[#0A0A0A]">{sections.find((c) => c.id === active)?.label || "Records"}</span>
              </div>
              {!loading && !error && (
                <span className="text-sm text-neutral-500" data-testid="record-count">
                  {DRILL[drillKey] && rows.length > 0 && <span className="mr-3 text-[#0055FF]">Click a row to open details</span>}
                  {count.toLocaleString()} total · showing {rows.length}
                </span>
              )}
            </div>

            {loading ? (
              <div className="flex h-64 items-center justify-center" data-testid="viewer-loading"><CircleNotch size={28} className="animate-spin text-[#0055FF]" /></div>
            ) : error ? (
              <div className="flex h-64 flex-col items-center justify-center gap-2 text-center" data-testid="viewer-error">
                <WarningCircle size={28} className="text-[#D97706]" />
                <p className="max-w-md text-sm text-neutral-600">{error}</p>
              </div>
            ) : rows.length === 0 ? (
              <div className="flex h-64 flex-col items-center justify-center gap-2" data-testid="viewer-empty">
                <Database size={28} className="text-neutral-300" />
                <p className="text-sm text-neutral-500">No records found for this source.</p>
              </div>
            ) : (
              <div className="overflow-x-auto" data-testid="viewer-table">
                <table className="w-full border-collapse text-sm">
                  <thead>
                    <tr className="border-b border-neutral-200 bg-neutral-50">
                      {columns.map((col) => (
                        <th key={col} className="whitespace-nowrap px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-neutral-500">{col}</th>
                      ))}
                      {DRILL[drillKey] && <th className="px-4 py-3" />}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, i) => {
                      const drillPath = DRILL[drillKey] && rawItems[i]?.id ? DRILL[drillKey](rawItems[i]) : null;
                      const rowKey = rawItems[i]?.id || `row-${i}`;
                      return (
                        <tr key={rowKey}
                          data-testid={drillPath ? `viewer-row-link-${rawItems[i].id}` : undefined}
                          onClick={drillPath ? () => navigate(drillPath) : undefined}
                          className={`border-b border-neutral-100 even:bg-neutral-50/60 hover:bg-[#0055FF]/[0.03] ${drillPath ? "cursor-pointer" : ""}`}>
                          {columns.map((col) => {
                            const val = row[col];
                            const numeric = typeof val === "number";
                            return (
                              <td key={col} className={`max-w-[280px] truncate whitespace-nowrap px-4 py-3 text-[#0A0A0A] ${numeric ? "text-right font-mono" : "text-left"}`} title={cellText(val)}>
                                {cellText(val)}
                              </td>
                            );
                          })}
                          {DRILL[drillKey] && (
                            <td className="px-4 py-3 text-right">{drillPath && <ArrowRight size={16} className="ml-auto text-neutral-300" />}</td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}
